import React from 'react';

export function SmartRechnrIllustration() {
  return (
    <div className="w-full h-full flex items-center justify-center p-4 select-none" id="smart-rechnr-vector-illustration">
      <svg
        viewBox="0 0 500 380"
        className="w-full h-full max-w-[420px]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Definitions for gorgeous gradients and filters */}
        <defs>
          <linearGradient id="glowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.05" />
          </linearGradient>
          
          <linearGradient id="gradientTeal" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0.2" />
          </linearGradient>

          <linearGradient id="gradientBlue" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.3" />
          </linearGradient>
          
          <linearGradient id="gradientGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.3" />
          </linearGradient>

          <linearGradient id="cardBgGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0f172a" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#1e293b" stopOpacity="0.1" />
          </linearGradient>

          <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="12" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <filter id="crispGlow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Global Styles for Keyframes Animations */}
        <style>{`
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(1.5deg); }
          }
          @keyframes float-medium {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-7px) rotate(-1.5deg); }
          }
          @keyframes pulse-glowing {
            0%, 100% { opacity: 0.25; }
            50% { opacity: 0.5; }
          }
          @keyframes dash-drawing {
            to { stroke-dashoffset: 0; }
          }
          .anim-float-1 { animation: float-slow 7s ease-in-out infinite; }
          .anim-float-2 { animation: float-medium 5s ease-in-out infinite; }
          .anim-pulse { animation: pulse-glowing 3s ease-in-out infinite; }
        `}</style>

        {/* Dynamic ambient background glow circles */}
        <g className="anim-pulse">
          <circle cx="250" cy="180" r="130" fill="url(#glowGrad)" filter="url(#softGlow)" />
          <circle cx="120" cy="130" r="40" fill="#06b6d4" fillOpacity="0.12" filter="url(#softGlow)" />
          <circle cx="360" cy="240" r="50" fill="#3b82f6" fillOpacity="0.12" filter="url(#softGlow)" />
        </g>

        {/* Decorative Grid Metrics */}
        <g opacity="0.15">
          <line x1="50" y1="50" x2="450" y2="50" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="50" y1="120" x2="450" y2="120" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="50" y1="190" x2="450" y2="190" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="50" y1="260" x2="450" y2="260" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="50" y1="330" x2="450" y2="330" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />

          <line x1="90" y1="30" x2="90" y2="350" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="210" y1="30" x2="210" y2="350" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="330" y1="30" x2="330" y2="350" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
          <line x1="410" y1="30" x2="410" y2="350" stroke="#94a3b8" strokeWidth="1" strokeDasharray="5,5" />
        </g>

        {/* --- MAIN BASE STRUCTURE: Transparent Dashboard Panel --- */}
        <g className="anim-float-1" transform="translate(10, 5)">
          {/* Main frame container */}
          <rect
            x="80"
            y="60"
            width="320"
            height="240"
            rx="24"
            fill="url(#cardBgGrad)"
            stroke="#38bdf8"
            strokeWidth="1.5"
            strokeOpacity="0.25"
            filter="url(#crispGlow)"
          />

          {/* Top Panel Border Accent Line */}
          <path
            d="M 104 60 L 376 60"
            stroke="url(#gradientBlue)"
            strokeWidth="3"
            strokeLinecap="round"
          />

          {/* Three small terminal dots at top left representing premium software system */}
          <circle cx="115" cy="80" r="5" fill="#f43f5e" fillOpacity="0.75" />
          <circle cx="130" cy="80" r="5" fill="#eab308" fillOpacity="0.75" />
          <circle cx="145" cy="80" r="5" fill="#10b981" fillOpacity="0.75" />
          <text x="165" y="84" fill="#94a3b8" fontSize="10" fontWeight="bold" letterSpacing="1" opacity="0.6">
            SMART-RECHNR ENGINE
          </text>

          {/* --- Nested Sub-device: Glassmorphic Calculator Grid --- */}
          <g transform="translate(100, 110)">
            {/* Calculator Outer outline */}
            <rect
              x="0"
              y="0"
              width="120"
              height="160"
              rx="16"
              fill="#0f172a"
              fillOpacity="0.5"
              stroke="#38bdf8"
              strokeWidth="1"
              strokeOpacity="0.3"
            />

            {/* Calculator Display Screen */}
            <rect
              x="10"
              y="12"
              width="100"
              height="28"
              rx="6"
              fill="#020617"
              fillOpacity="0.75"
              stroke="#0ea5e9"
              strokeWidth="1"
              strokeOpacity="0.4"
            />
            {/* Calculated output placeholder display */}
            <text x="18" y="30" fill="#38bdf8" fontSize="12" fontWeight="bold" fontFamily="monospace" letterSpacing="1">
              8.342,00 �
            </text>

            {/* Keyboard Key Grids Matrix */}
            <g transform="translate(10, 48)">
              {/* Row 1 */}
              <rect x="0" y="5" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="8" y="17" fill="#94a3b8" fontSize="9" fontWeight="bold">7</text>

              <rect x="26" y="5" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="34" y="17" fill="#94a3b8" fontSize="9" fontWeight="bold">8</text>

              <rect x="52" y="5" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="60" y="17" fill="#94a3b8" fontSize="9" fontWeight="bold">9</text>

              <rect x="78" y="5" width="22" height="18" rx="4" fill="url(#gradientTeal)" />
              <text x="86" y="17" fill="#ffffff" fontSize="9" fontWeight="bold">�</text>

              {/* Row 2 */}
              <rect x="0" y="27" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="8" y="39" fill="#94a3b8" fontSize="9" fontWeight="bold">4</text>

              <rect x="26" y="27" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="34" y="39" fill="#94a3b8" fontSize="9" fontWeight="bold">5</text>

              <rect x="52" y="27" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="60" y="39" fill="#94a3b8" fontSize="9" fontWeight="bold">6</text>

              <rect x="78" y="27" width="22" height="18" rx="4" fill="url(#gradientTeal)" />
              <text x="85" y="39" fill="#ffffff" fontSize="9" fontWeight="bold">�</text>

              {/* Row 3 */}
              <rect x="0" y="49" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="8" y="61" fill="#94a3b8" fontSize="9" fontWeight="bold">1</text>

              <rect x="26" y="49" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="34" y="61" fill="#94a3b8" fontSize="9" fontWeight="bold">2</text>

              <rect x="52" y="49" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="60" y="61" fill="#94a3b8" fontSize="9" fontWeight="bold">3</text>

              <rect x="78" y="49" width="22" height="18" rx="4" fill="url(#gradientTeal)" />
              <text x="85" y="61" fill="#ffffff" fontSize="9" fontWeight="bold">-</text>

              {/* Row 4 */}
              <rect x="0" y="71" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="8" y="83" fill="#94a3b8" fontSize="9" fontWeight="bold">0</text>

              <rect x="26" y="71" width="22" height="18" rx="4" fill="#1e293b" fillOpacity="0.8" />
              <text x="35" y="83" fill="#94a3b8" fontSize="9" fontWeight="bold">.</text>

              <rect x="52" y="71" width="48" height="18" rx="4" fill="url(#gradientBlue)" />
              <text x="71" y="83" fill="#ffffff" fontSize="9" fontWeight="bold">=</text>
            </g>
          </g>

          {/* --- Nested Sub-device: Accurate Glowing Line Chart --- */}
          <g transform="translate(235, 120)">
            <text x="5" y="0" fill="#94a3b8" fontSize="10" fontWeight="bold" opacity="0.8">
              ZINS & STEUER METRIC
            </text>
            
            {/* Chart Axes lines */}
            <line x1="0" y1="100" x2="135" y2="100" stroke="#475569" strokeWidth="1" />
            <line x1="0" y1="10" x2="0" y2="100" stroke="#475569" strokeWidth="1" />

            {/* Glowing Chart Path Area (gently drawn) */}
            <path
              d="M 2 85 Q 25 50 50 65 T 100 25 T 130 15"
              fill="none"
              stroke="url(#gradientTeal)"
              strokeWidth="3"
              strokeLinecap="round"
              filter="url(#crispGlow)"
            />

            {/* Glowing dots on chart peaks */}
            <circle cx="50" cy="65" r="4" fill="#2dd4bf" />
            <circle cx="100" cy="25" r="4" fill="#2dd4bf" />
            <circle cx="130" cy="15" r="5" fill="#38bdf8" />
            <circle cx="130" cy="15" r="1.5" fill="#ffffff" />

            {/* Trend percentage indicator chip */}
            <rect x="80" y="72" width="50" height="18" rx="6" fill="#020617" fillOpacity="0.8" stroke="#10b981" strokeOpacity="0.3" strokeWidth="1" />
            <text x="89" y="84" fill="#10b981" fontSize="9" fontWeight="bold">+14.2%</text>
          </g>
        </g>

        {/* --- FLOATING CHIPS & SYMBOLS (TRUE TRANSPARENT BACKGROUND LOOK) --- */}
        {/* Glowing floating percentage tag "%" */}
        <g className="anim-float-2" transform="translate(60, 150)">
          <rect
            width="42"
            height="42"
            rx="12"
            fill="#0f172a"
            fillOpacity="0.75"
            stroke="url(#gradientGold)"
            strokeWidth="1.5"
            filter="url(#crispGlow)"
          />
          <text x="13" y="27" fill="#f59e0b" fontSize="18" fontWeight="bold">%</text>
        </g>

        {/* Floating Money tag "�" */}
        <g className="anim-float-1" transform="translate(380, 80)">
          <rect
            width="44"
            height="44"
            rx="14"
            fill="#0f172a"
            fillOpacity="0.7"
            stroke="url(#gradientTeal)"
            strokeWidth="1.5"
            filter="url(#crispGlow)"
          />
          <text x="15" y="28" fill="#2dd4bf" fontSize="18" fontWeight="bold">�</text>
        </g>

        {/* Floating Sum sign / math "S" */}
        <g className="anim-float-2" transform="translate(250, 20)">
          <rect
            width="34"
            height="34"
            rx="10"
            fill="#0f172a"
            fillOpacity="0.65"
            stroke="url(#gradientBlue)"
            strokeWidth="1"
          />
          <text x="11" y="23" fill="#38bdf8" fontSize="14" fontWeight="bold">S</text>
        </g>

        {/* Sparkle decorative icons representing AI and precision */}
        <g className="anim-float-1" transform="translate(390, 270)">
          <path
            d="M 12 0 L 15 9 L 24 12 L 15 15 L 12 24 L 9 15 L 0 12 L 9 9 Z"
            fill="#f59e0b"
            fillOpacity="0.8"
            transform="scale(0.8)"
          />
        </g>
        
        <g className="anim-float-2" transform="translate(50, 70)">
          <path
            d="M 12 0 L 15 9 L 24 12 L 15 15 L 12 24 L 9 15 L 0 12 L 9 9 Z"
            fill="#38bdf8"
            fillOpacity="0.75"
            transform="scale(0.6)"
          />
        </g>
      </svg>
    </div>
  );
}
